import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CalendarIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    TicketIcon,
    ArrowRightIcon,
    PencilSquareIcon,
    TrashIcon,
    PlusIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CalendarWidget } from '../ui/calendar-widget';
import { ExpandableScreen, ExpandableScreenContent } from '../ui/expandable-screen';
import { DetailSheet } from '../ui/detail-sheet';
import { NotesApp } from '../ui/notes-app';
import { type Bill, dueDatesInRange, fetchBills, PRIORITY_TEXT } from '../../lib/bills';

interface CalendarBooking {
    id: string;
    name: string;
    business_name?: string | null;
    email: string;
    event_type: string;
    event_date: string;
    start_time: string | null;
    end_time: string | null;
    location: string | null;
    status: string;
    retainer: boolean;
}
interface CalendarEventRow {
    id: string;
    title: string;
    event_date: string;
    status: string;
    location: string | null;
    image_url: string | null;
}
interface CalendarPhoto {
    id: string;
    title: string;
    thumbnail_url: string;
    created_at: string;
}
interface CalendarNote {
    id: string;
    date: string;
    note: string;
    created_at: string;
}
interface CrewBlock {
    id: string;
    date: string;
    note: string | null;
    photographerId: string | null;
    photographerName: string;
}
interface CalendarRangeResponse {
    range: { start: string; end: string };
    bookings: CalendarBooking[];
    events: CalendarEventRow[];
    adminBlocked: Array<{ date: string; note: string | null }>;
    crewBlocked: CrewBlock[];
    notes: CalendarNote[];
    photos: Record<string, CalendarPhoto[]>;
    summary: { bookingCount: number; eventCount: number; photoCount: number; blockedCount: number; crewBlockedCount?: number; noteCount: number };
}

/** The active roster, so a day can say who IS free as well as who isn't. */
interface CrewMember {
    id: string;
    name: string;
    email: string;
}

type ViewMode = 'month' | 'week' | 'day';

function toYMD(d: Date): string {
    return d.toLocaleDateString('en-CA');
}

function monthWindow(viewDate: Date): { start: string; end: string } {
    const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const last = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    return { start: toYMD(first), end: toYMD(last) };
}

function formatDateHuman(ymd: string): string {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
}

function datesInRange(start: string, end: string): string[] {
    const [ys, ms, ds] = start.split('-').map(Number);
    const [ye, me, de] = end.split('-').map(Number);
    const s = new Date(ys, ms - 1, ds);
    const e = new Date(ye, me - 1, de);
    const out: string[] = [];
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        out.push(toYMD(d));
    }
    return out;
}

function getWeekStart(d: Date): Date {
    const date = new Date(d);
    date.setDate(date.getDate() - date.getDay());
    date.setHours(0, 0, 0, 0);
    return date;
}

export default function AdminCalendarView({ adminEmail }: { adminEmail?: string } = {}) {
    const [viewMode, setViewMode] = useState<ViewMode>('month');
    // The active roster, so "who is free on the 3rd?" can be answered by name
    // rather than by the absence of a row. Loaded once — names change far more
    // slowly than the calendar window does.
    const [crew, setCrew] = useState<CrewMember[]>([]);
    const [focusDate, setFocusDate] = useState<Date>(new Date());
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [monthCursor, setMonthCursor] = useState<Date>(new Date());
    const [monthData, setMonthData] = useState<CalendarRangeResponse | null>(null);
    // Bills and loans, for the red due-date dots. Admin-gated, so a failure
    // just leaves the dots off rather than breaking the calendar.
    const [bills, setBills] = useState<Bill[]>([]);
    useEffect(() => { fetchBills().then(setBills).catch(() => setBills([])); }, []);
    const [detailData, setDetailData] = useState<CalendarRangeResponse | null>(null);
    const [monthLoading, setMonthLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [reviewExpanded, setReviewExpanded] = useState<'collapsed' | 'glance' | 'deep'>('glance');

    useEffect(() => {
        const { start, end } = monthWindow(monthCursor);
        loadMonth(start, end);
    }, [monthCursor]);

    useEffect(() => {
        let alive = true;
        const today = toYMD(new Date());
        // Range is irrelevant here — we only want the roster names back. The
        // per-date blocks come from /api/calendar/range with everything else.
        fetch(`/api/crew/availability?roster=1&start=${today}&end=${today}`, {
            headers: adminEmail ? { 'x-admin-email': adminEmail } : {},
        })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (alive && d?.roster) setCrew(d.roster.map((p: any) => ({ id: p.id, name: p.name, email: p.email }))); })
            .catch(() => { /* the crew panel degrades to "out" only */ });
        return () => { alive = false; };
    }, [adminEmail]);

    useEffect(() => {
        if (viewMode === 'week') {
            const ws = getWeekStart(focusDate);
            const we = new Date(ws);
            we.setDate(we.getDate() + 6);
            loadDetail(toYMD(ws), toYMD(we));
        } else if (viewMode === 'day') {
            const d = toYMD(focusDate);
            loadDetail(d, d);
        }
    }, [viewMode, focusDate]);

    async function loadMonth(start: string, end: string) {
        setMonthLoading(true);
        try {
            const res = await fetch(`/api/calendar/range?start=${start}&end=${end}`);
            if (res.ok) setMonthData(await res.json());
        } finally {
            setMonthLoading(false);
        }
    }

    async function loadDetail(start: string, end: string) {
        setDetailLoading(true);
        try {
            const res = await fetch(`/api/calendar/range?start=${start}&end=${end}`);
            if (res.ok) setDetailData(await res.json());
        } finally {
            setDetailLoading(false);
        }
    }

    // Refresh whichever windows are currently in scope. Called by booking
    // action buttons after they mutate state, so the cards re-render with
    // the new status.
    const refreshAll = useCallback(() => {
        const { start, end } = monthWindow(monthCursor);
        loadMonth(start, end);
        if (viewMode === 'week') {
            const ws = getWeekStart(focusDate);
            const we = new Date(ws);
            we.setDate(we.getDate() + 6);
            loadDetail(toYMD(ws), toYMD(we));
        } else if (viewMode === 'day') {
            loadDetail(toYMD(focusDate), toYMD(focusDate));
        } else if (startDate && endDate) {
            loadDetail(toYMD(startDate), toYMD(endDate));
        }
    }, [monthCursor, viewMode, focusDate, startDate, endDate]);

    const activityByDate = useMemo(() => {
        const m = new Map<string, { bookings: number; events: number; photos: number; blocked: boolean; crewOut: number; bills: number }>();
        const ensure = (d: string) => {
            if (!m.has(d)) m.set(d, { bookings: 0, events: 0, photos: 0, blocked: false, crewOut: 0, bills: 0 });
            return m.get(d)!;
        };
        if (!monthData) return m;
        for (const b of monthData.bookings) ensure(b.event_date).bookings++;
        for (const e of monthData.events) ensure(e.event_date).events++;
        for (const d of Object.keys(monthData.photos)) ensure(d).photos += monthData.photos[d].length;
        for (const a of monthData.adminBlocked) ensure(a.date).blocked = true;
        for (const c of monthData.crewBlocked || []) ensure(c.date).crewOut++;
        const { start, end } = monthWindow(monthCursor);
        for (const b of bills) for (const d of dueDatesInRange(b, start, end)) ensure(d).bills++;
        return m;
    }, [monthData, bills, monthCursor]);

    function handleDateClick(date: Date) {
        const ymd = toYMD(date);
        if (!startDate || (startDate && endDate)) {
            setStartDate(date);
            setEndDate(date);
            setFocusDate(date);
            setReviewExpanded('glance');
        } else {
            if (ymd === toYMD(startDate)) {
                setStartDate(null);
                setEndDate(null);
            } else if (date < startDate) {
                setEndDate(startDate);
                setStartDate(date);
                setReviewExpanded('glance');
            } else {
                setEndDate(date);
                setReviewExpanded('glance');
            }
        }
    }

    function handleSwitchMode(m: ViewMode) {
        setViewMode(m);
        if (m !== 'month') {
            setFocusDate(startDate ?? new Date());
        }
    }

    const selectedRange = useMemo(() => {
        if (!startDate) return null;
        const s = toYMD(startDate);
        const e = endDate ? toYMD(endDate) : s;
        return { start: s, end: e };
    }, [startDate, endDate]);

    const review = useMemo(() => {
        if (!selectedRange || !monthData) return null;
        const days = new Set(datesInRange(selectedRange.start, selectedRange.end));
        const bookings = monthData.bookings.filter(b => days.has(b.event_date));
        const events = monthData.events.filter(e => days.has(e.event_date));
        const notes = (monthData.notes || []).filter(n => days.has(n.date));
        const photos: CalendarPhoto[] = [];
        for (const d of days) {
            const list = monthData.photos[d];
            if (list) photos.push(...list);
        }
        return { bookings, events, photos, notes };
    }, [selectedRange, monthData]);

    async function addNote(date: string, note: string) {
        await fetch('/api/calendar/notes', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ date, note }),
        });
        const { start, end } = monthWindow(monthCursor);
        loadMonth(start, end);
        if (viewMode !== 'month') loadDetail(toYMD(focusDate), toYMD(focusDate));
    }

    async function deleteNote(id: string) {
        await fetch(`/api/calendar/notes?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
        const { start, end } = monthWindow(monthCursor);
        loadMonth(start, end);
        if (viewMode !== 'month') loadDetail(toYMD(focusDate), toYMD(focusDate));
    }

    const summaryData = viewMode === 'month' ? monthData : detailData;
    const isLoading = viewMode === 'month' ? monthLoading : detailLoading;
    const [notesOpen, setNotesOpen] = useState(false);

    function handleRefresh() {
        if (viewMode === 'month') {
            const { start, end } = monthWindow(monthCursor);
            loadMonth(start, end);
        } else if (viewMode === 'week') {
            const ws = getWeekStart(focusDate);
            const we = new Date(ws); we.setDate(we.getDate() + 6);
            loadDetail(toYMD(ws), toYMD(we));
        } else {
            loadDetail(toYMD(focusDate), toYMD(focusDate));
        }
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        Master Calendar
                    </h3>
                    <p className="text-white/30 text-xs mt-1 hidden sm:block">
                        Site-wide activity — bookings, events, uploads.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <button data-tour="cal-notes"
                        onClick={() => setNotesOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-white/10 text-white hover:bg-white hover:text-black transition-colors"
                        style={{ borderRadius: 0 }}
                    >
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                        Notes
                    </button>
                    <button
                        onClick={handleRefresh}
                        className="text-white/40 hover:text-white transition-colors"
                        title="Refresh"
                    >
                        <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* The notes app rides along inside the master calendar as its own
                expandable card — same shell every widget's detail view uses. */}
            {notesOpen && (
                <DetailSheet onClose={() => setNotesOpen(false)} label="Close notes" wide>
                    <h2 className="text-lg font-black uppercase tracking-widest text-white pr-10 mb-4 text-left">Notes</h2>
                    <NotesApp />
                </DetailSheet>
            )}

            {/* View mode toggle */}
            <div data-tour="cal-modes" className="flex border border-white/10">
                {(['month', 'week', 'day'] as ViewMode[]).map(m => (
                    <button
                        key={m}
                        onClick={() => handleSwitchMode(m)}
                        className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                            viewMode === m
                                ? 'bg-white text-black'
                                : 'text-white/40 hover:text-white hover:bg-white/5'
                        }`}
                    >
                        {m}
                    </button>
                ))}
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <SummaryCard label="Bookings" value={summaryData?.summary.bookingCount ?? 0} accent="yellow" />
                <SummaryCard label="Events" value={summaryData?.summary.eventCount ?? 0} accent="green" />
                <SummaryCard label="Uploads" value={summaryData?.summary.photoCount ?? 0} accent="white" />
            </div>

            {/* MONTH VIEW */}
            {viewMode === 'month' && (
                <div className="space-y-4">
                    <div data-tour="cal-month" className="bg-white/[0.02] p-4 sm:p-6">
                        <CalendarWidget
                            size="large"
                            startDate={startDate}
                            endDate={endDate}
                            interactive
                            onDateSelect={handleDateClick}
                            viewDate={monthCursor}
                            onMonthChange={setMonthCursor}
                            dotsByDate={activityByDate}
                        />
                        <ActivityLegend />
                        {selectedRange && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                                <p className="text-white/60 text-xs">
                                    {selectedRange.start === selectedRange.end
                                        ? formatDateHuman(selectedRange.start)
                                        : `${formatDateHuman(selectedRange.start)} → ${formatDateHuman(selectedRange.end)}`}
                                </p>
                                <div className="flex items-center gap-3">
                                    {selectedRange.start === selectedRange.end && (
                                        <button data-tour="cal-open-day"
                                            onClick={() => { setFocusDate(startDate!); setViewMode('day'); }}
                                            className="text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors"
                                        >
                                            Day View →
                                        </button>
                                    )}
                                    <button
                                        onClick={() => { setStartDate(null); setEndDate(null); }}
                                        className="text-white/40 hover:text-white text-[10px] font-bold uppercase tracking-widest transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {selectedRange && review && (
                        <TimeInReviewCard
                            range={selectedRange}
                            review={review}
                            expanded={reviewExpanded}
                            onToggle={() => setReviewExpanded(prev =>
                                prev === 'collapsed' ? 'glance' : prev === 'glance' ? 'deep' : 'collapsed'
                            )}
                            onAddNote={addNote}
                            onDeleteNote={deleteNote}
                            onBookingChange={refreshAll}
                        />
                    )}
                </div>
            )}

            {/* WEEK VIEW */}
            {viewMode === 'week' && (
                <WeekView
                    focusDate={focusDate}
                    data={detailData}
                    loading={detailLoading}
                    onDayClick={(date) => { setFocusDate(date); setViewMode('day'); }}
                    onPrevWeek={() => { const d = new Date(focusDate); d.setDate(d.getDate() - 7); setFocusDate(d); }}
                    onNextWeek={() => { const d = new Date(focusDate); d.setDate(d.getDate() + 7); setFocusDate(d); }}
                />
            )}

            {/* DAY VIEW */}
            {viewMode === 'day' && (
                <DayView
                    focusDate={focusDate}
                    data={detailData}
                    crew={crew}
                    loading={detailLoading}
                    bills={bills}
                    onPrevDay={() => { const d = new Date(focusDate); d.setDate(d.getDate() - 1); setFocusDate(d); }}
                    onNextDay={() => { const d = new Date(focusDate); d.setDate(d.getDate() + 1); setFocusDate(d); }}
                    onAddNote={addNote}
                    onDeleteNote={deleteNote}
                    onBookingChange={refreshAll}
                />
            )}
        </div>
    );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ label, value, accent }: { label: string; value: number; accent: 'yellow' | 'green' | 'white' }) {
    const accentColor = accent === 'yellow' ? 'text-yellow-400' : accent === 'green' ? 'text-green-400' : 'text-white';
    return (
        <div className="bg-white/[0.03] p-3 sm:p-4">
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">{label}</p>
            <p className={`text-2xl sm:text-3xl font-black ${accentColor}`}>{value.toLocaleString()}</p>
        </div>
    );
}

function ActivityLegend() {
    return (
        <div className="flex flex-wrap items-center gap-4 mt-4 text-[9px] font-black uppercase tracking-widest text-white/40">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-yellow-400 rounded-full" /> Booking</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-green-400 rounded-full" /> Event</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-white/60 rounded-full" /> Upload</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-red-400 rounded-full" /> Bill Due</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-white/25 rounded-full" /> Blocked</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full" /> Crew Out</span>
        </div>
    );
}

/**
 * Who can shoot on a given day.
 *
 * Reads as availability rather than as a block list on purpose: the useful
 * answer to "can we take Thursday?" is a name you can call, so the people who
 * are free are listed first and the people who are out are the exception
 * underneath. Nobody being out is stated explicitly — a silent section would be
 * indistinguishable from one that failed to load.
 */
function CrewAvailability({ crew, blocks }: { crew: CrewMember[]; blocks: CrewBlock[] }) {
    const outIds = new Set(blocks.map(b => b.photographerId).filter(Boolean) as string[]);
    const available = crew.filter(person => !outIds.has(person.id));

    return (
        <Section title={`Crew (${available.length} available)`}>
            {crew.length === 0 && blocks.length === 0 ? (
                <EmptyLine>Roster unavailable.</EmptyLine>
            ) : (
                <div className="space-y-2">
                    {available.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {available.map(person => (
                                <span key={person.id} className="bg-green-500/15 text-green-300 px-2 py-1 text-[10px] font-black uppercase tracking-widest">
                                    {person.name}
                                </span>
                            ))}
                        </div>
                    )}
                    {blocks.length === 0 ? (
                        <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold">Nobody has blocked this day out.</p>
                    ) : (
                        <div className="space-y-px">
                            {blocks.map(block => (
                                <div key={block.id} className="bg-amber-500/10 px-3 py-2">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-amber-300">
                                        {block.photographerName} — unavailable
                                    </p>
                                    {block.note && <p className="text-xs text-white/50 mt-1 text-left">{block.note}</p>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </Section>
    );
}

// ─── Week View ─────────────────────────────────────────────────────────────────

function WeekView({
    focusDate,
    data,
    loading,
    onDayClick,
    onPrevWeek,
    onNextWeek,
}: {
    focusDate: Date;
    data: CalendarRangeResponse | null;
    loading: boolean;
    onDayClick: (d: Date) => void;
    onPrevWeek: () => void;
    onNextWeek: () => void;
}) {
    const weekStart = getWeekStart(focusDate);
    const days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
    });
    const focusYmd = toYMD(focusDate);
    const todayYmd = toYMD(new Date());
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const weekLabel = `${days[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${days[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <button onClick={onPrevWeek} className="text-white/40 hover:text-white transition-colors p-2">
                    <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <p className="text-[10px] font-black uppercase tracking-widest text-white/60">{weekLabel}</p>
                <button onClick={onNextWeek} className="text-white/40 hover:text-white transition-colors p-2">
                    <ChevronRightIcon className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="text-white/30 text-xs text-center py-12">Loading…</div>
            ) : (
                <div className="grid grid-cols-7 gap-px bg-white/[0.06]">
                    {days.map((d, i) => {
                        const ymd = toYMD(d);
                        const dayBookings = data?.bookings.filter(b => b.event_date === ymd) || [];
                        const dayEvents = data?.events.filter(e => e.event_date === ymd) || [];
                        const dayPhotos = (data?.photos[ymd] || []);
                        const dayCrewOut = data?.crewBlocked?.filter(c => c.date === ymd) || [];
                        const isToday = ymd === todayYmd;
                        const isFocus = ymd === focusYmd;

                        return (
                            <button data-tour="cal-day"
                                key={ymd}
                                onClick={() => onDayClick(d)}
                                className={`bg-black flex flex-col min-h-[120px] sm:min-h-[160px] p-1.5 sm:p-2 text-left hover:bg-white/[0.04] transition-colors ${isFocus ? 'ring-1 ring-inset ring-white/20' : ''}`}
                            >
                                <div className="mb-1.5">
                                    <p className="text-[8px] sm:text-[9px] text-white/30 font-black uppercase tracking-widest">{dayNames[i]}</p>
                                    <p className={`text-base sm:text-lg font-bold leading-none mt-0.5 ${isToday ? 'text-white' : 'text-white/50'}`}>{d.getDate()}</p>
                                </div>

                                <div className="flex-1 space-y-0.5 overflow-hidden">
                                    {dayBookings.slice(0, 3).map(b => (
                                        <div key={b.id} className="bg-yellow-500/20 px-1 py-0.5">
                                            <p className="text-[8px] text-yellow-300 font-bold truncate leading-none">{b.name}</p>
                                        </div>
                                    ))}
                                    {dayBookings.length > 3 && (
                                        <p className="text-[8px] text-yellow-400/50 px-1">+{dayBookings.length - 3}</p>
                                    )}
                                    {dayEvents.slice(0, 2).map(e => (
                                        <div key={e.id} className="bg-green-500/20 px-1 py-0.5">
                                            <p className="text-[8px] text-green-300 font-bold truncate leading-none">{e.title}</p>
                                        </div>
                                    ))}
                                    {dayEvents.length > 2 && (
                                        <p className="text-[8px] text-green-400/50 px-1">+{dayEvents.length - 2}</p>
                                    )}
                                    {dayCrewOut.slice(0, 2).map(c => (
                                        <div key={c.id} className="bg-amber-500/20 px-1 py-0.5">
                                            <p className="text-[8px] text-amber-300 font-bold truncate leading-none">{c.photographerName} out</p>
                                        </div>
                                    ))}
                                    {dayCrewOut.length > 2 && (
                                        <p className="text-[8px] text-amber-400/50 px-1">+{dayCrewOut.length - 2} out</p>
                                    )}
                                </div>

                                {dayPhotos.length > 0 && (
                                    <p className="text-[8px] text-white/20 mt-1">{dayPhotos.length} ↑</p>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Day View ──────────────────────────────────────────────────────────────────

function DayView({
    focusDate,
    data,
    crew,
    loading,
    onPrevDay,
    onNextDay,
    onAddNote,
    onDeleteNote,
    onBookingChange,
    bills = [],
}: {
    focusDate: Date;
    data: CalendarRangeResponse | null;
    crew: CrewMember[];
    loading: boolean;
    bills?: Bill[];
    onPrevDay: () => void;
    onNextDay: () => void;
    onAddNote: (date: string, note: string) => void;
    onDeleteNote: (id: string) => void;
    onBookingChange?: () => void;
}) {
    const ymd = toYMD(focusDate);
    const [addingBooking, setAddingBooking] = useState(false);
    // The day's business recap — sales with their money trail, and what shipped.
    const [recap, setRecap] = useState<RecapResponse | null>(null);
    const [recapLoading, setRecapLoading] = useState(false);
    useEffect(() => {
        let alive = true;
        setRecapLoading(true);
        setRecap(null);
        fetch(`/api/calendar/recap?date=${ymd}`)
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (alive) setRecap(d); })
            .catch(() => { /* the section shows its own unavailable state */ })
            .finally(() => { if (alive) setRecapLoading(false); });
        return () => { alive = false; };
    }, [ymd]);
    const bookings = data?.bookings.filter(b => b.event_date === ymd) || [];
    const events = data?.events.filter(e => e.event_date === ymd) || [];
    const photos = data?.photos[ymd] || [];
    const notes = data?.notes.filter(n => n.date === ymd) || [];
    const crewOut = data?.crewBlocked?.filter(c => c.date === ymd) || [];
    const billsDue = bills.filter(b => dueDatesInRange(b, ymd, ymd).length > 0);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between bg-white/[0.02] px-4 py-3">
                <button onClick={onPrevDay} className="text-white/40 hover:text-white transition-colors p-1">
                    <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <div className="text-center">
                    <p className="text-sm font-bold text-white">{formatDateHuman(ymd)}</p>
                    <p className="text-[9px] text-white/30 font-mono mt-0.5">{ymd}</p>
                </div>
                <button onClick={onNextDay} className="text-white/40 hover:text-white transition-colors p-1">
                    <ChevronRightIcon className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="text-white/30 text-xs text-center py-12">Loading…</div>
            ) : (
                <div className="space-y-6">
                    <DailyRecap recap={recap} loading={recapLoading} />

                    {billsDue.length > 0 && (
                        <Section title={`Bills Due (${billsDue.length})`}>
                            <div className="space-y-1">
                                {billsDue.map(b => (
                                    <div key={b.id} className="flex items-center gap-3 bg-red-400/10 px-3 py-2 text-xs">
                                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full shrink-0" />
                                        <span className="flex-1 min-w-0 truncate text-white">{b.name}</span>
                                        <span className={`text-[9px] font-black uppercase tracking-widest ${PRIORITY_TEXT[b.priority]}`}>{b.priority}</span>
                                        <span className="font-bold tabular-nums text-white">
                                            {b.amount_cents ? `$${(b.amount_cents / 100).toFixed(2)}` : 'Amount TBD'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}

                    <Section
                        title={`Bookings (${bookings.length})`}
                        action={
                            <button data-tour="cal-add-booking"
                                onClick={() => setAddingBooking(v => !v)}
                                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors"
                            >
                                <PlusIcon className="w-3 h-3" />
                                {addingBooking ? 'Cancel' : 'Add Booking'}
                            </button>
                        }
                    >
                        {addingBooking && (
                            <AddBookingForm
                                date={ymd}
                                onDone={() => { setAddingBooking(false); onBookingChange?.(); }}
                            />
                        )}
                        {bookings.length === 0 && !addingBooking ? (
                            <EmptyLine>No bookings on this day.</EmptyLine>
                        ) : (
                            <div className="space-y-px">
                                {bookings.map(b => (
                                    <ExpandableBookingCard key={b.id} booking={b} onChange={onBookingChange} />
                                ))}
                            </div>
                        )}
                    </Section>

                    <CrewAvailability crew={crew} blocks={crewOut} />

                    <Section title={`Events (${events.length})`}>
                        {events.length === 0 ? (
                            <EmptyLine>No events on this day.</EmptyLine>
                        ) : (
                            <div className="space-y-px">
                                {events.map(e => (
                                    <ExpandableEventCard key={e.id} event={e} />
                                ))}
                            </div>
                        )}
                    </Section>

                    {photos.length > 0 && (
                        <Section title={`Uploads (${photos.length})`}>
                            <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
                                {photos.map(p => (
                                    <img key={p.id} src={p.thumbnail_url} alt={p.title} className="w-full aspect-square object-cover bg-white/5" />
                                ))}
                            </div>
                        </Section>
                    )}

                    <NotesSection
                        range={{ start: ymd, end: ymd }}
                        notes={notes}
                        onAdd={onAddNote}
                        onDelete={onDeleteNote}
                    />
                </div>
            )}
        </div>
    );
}

// ─── Expandable Cards ──────────────────────────────────────────────────────────

function ExpandableBookingCard({ booking: b, onChange }: { booking: CalendarBooking; onChange?: () => void }) {
    const [busy, setBusy] = useState<null | 'confirm' | 'decline' | 'cancel'>(null);
    const [error, setError] = useState<string | null>(null);

    async function setStatus(next: 'confirmed' | 'declined' | 'cancelled', notify: boolean) {
        const labelMap = { confirmed: 'confirm', declined: 'decline', cancelled: 'cancel' } as const;
        setBusy(labelMap[next]);
        setError(null);
        try {
            const res = await fetch('/api/booking?action=admin', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: b.id, status: next, notify_customer: notify }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            onChange?.();
        } catch (e: any) {
            setError(e?.message || 'Failed');
        } finally {
            setBusy(null);
        }
    }

    const isPending = b.status === 'pending';
    const isConfirmed = b.status === 'confirmed';
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="contents">
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="w-full flex items-center gap-3 p-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors select-none text-left"
            >
                <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">
                        {b.name}{b.business_name ? ` · ${b.business_name}` : ''}
                    </p>
                    <p className="text-white/40 text-[10px] font-mono truncate">
                        {b.event_type}
                        {b.start_time ? ` · ${b.start_time.slice(0, 5)}–${b.end_time?.slice(0, 5) || '?'}` : ''}
                        {b.location ? ` · ${b.location}` : ''}
                    </p>
                </div>
                <StatusPill status={b.status} />
                <ChevronDownIcon className="w-3.5 h-3.5 text-white/30 flex-shrink-0 -rotate-90" />
            </button>

            <ExpandableScreen isOpen={isOpen} onOpenChange={setIsOpen}>
                <ExpandableScreenContent className="overflow-x-hidden">
                    <div className="flex-1 overflow-y-auto overflow-x-hidden">
                        <div className="max-w-2xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
                            <div className="flex items-center gap-3 mb-1">
                                <h2 className="text-lg font-black text-white truncate">
                                    {b.name}{b.business_name ? ` · ${b.business_name}` : ''}
                                </h2>
                                <StatusPill status={b.status} />
                            </div>
                            <p className="text-white/40 text-xs font-mono mb-8">{b.event_type}</p>

                            <div className="space-y-1.5">
                                <DetailRow label="Email" value={b.email} />
                                {b.location && <DetailRow label="Location" value={b.location} />}
                                {b.start_time && (
                                    <DetailRow label="Time" value={`${b.start_time.slice(0, 5)} – ${b.end_time?.slice(0, 5) || 'TBD'}`} />
                                )}
                                <DetailRow label="Retainer" value={b.retainer ? 'Paid' : 'Not paid'} />
                                <DetailRow label="Date" value={b.event_date} />

                                {/* Action bar */}
                                <div className="flex flex-wrap items-center gap-2 pt-6 mt-4 bg-white/5 p-3">
                                    {isPending && (
                                        <>
                                            <ActionButton
                                                tone="confirm"
                                                disabled={busy !== null}
                                                loading={busy === 'confirm'}
                                                onClick={() => setStatus('confirmed', true)}
                                            >
                                                Approve
                                            </ActionButton>
                                            <ActionButton
                                                tone="decline"
                                                disabled={busy !== null}
                                                loading={busy === 'decline'}
                                                onClick={() => setStatus('declined', true)}
                                            >
                                                Reject
                                            </ActionButton>
                                        </>
                                    )}
                                    {isConfirmed && (
                                        <ActionButton
                                            tone="decline"
                                            disabled={busy !== null}
                                            loading={busy === 'cancel'}
                                            onClick={() => setStatus('cancelled', true)}
                                        >
                                            Cancel booking
                                        </ActionButton>
                                    )}
                                    <a
                                        href={`/admin?section=invoices&booking_id=${encodeURIComponent(b.id)}&new=1`}
                                        className="px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest bg-white/5 text-white/80 hover:bg-white hover:text-black transition-colors"
                                    >
                                        Create invoice
                                    </a>
                                    <a
                                        href={`/admin/bookings?id=${encodeURIComponent(b.id)}`}
                                        className="px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors"
                                    >
                                        Open →
                                    </a>
                                    {error && (
                                        <span className="text-[10px] text-red-400 font-mono">{error}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </ExpandableScreenContent>
            </ExpandableScreen>
        </div>
    );
}

function ActionButton({
    tone,
    disabled,
    loading,
    onClick,
    children,
}: {
    tone: 'confirm' | 'decline';
    disabled?: boolean;
    loading?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    children: React.ReactNode;
}) {
    const toneClass =
        tone === 'confirm'
            ? 'bg-green-600 text-white hover:bg-green-500 border border-green-500/40'
            : 'bg-red-600/90 text-white hover:bg-red-500 border border-red-500/40';
    return (
        <button
            disabled={disabled}
            onClick={onClick}
            className={`px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${toneClass}`}
        >
            {loading ? '…' : children}
        </button>
    );
}

function ExpandableEventCard({ event: e }: { event: CalendarEventRow }) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="contents">
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="w-full flex items-center gap-3 p-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors select-none text-left"
            >
                <TicketIcon className="w-3.5 h-3.5 text-white/30 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold truncate">{e.title}</p>
                    {e.location && (
                        <p className="text-white/40 text-[10px] font-mono truncate">{e.location}</p>
                    )}
                </div>
                <StatusPill status={e.status} />
                <ChevronDownIcon className="w-3.5 h-3.5 text-white/30 flex-shrink-0 -rotate-90" />
            </button>

            <ExpandableScreen isOpen={isOpen} onOpenChange={setIsOpen}>
                <ExpandableScreenContent className="overflow-x-hidden">
                    <div className="flex-1 overflow-y-auto overflow-x-hidden">
                        <div className="max-w-2xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
                            <div className="flex items-center gap-3 mb-8">
                                <TicketIcon className="w-5 h-5 text-white/40" />
                                <h2 className="text-lg font-black text-white truncate">{e.title}</h2>
                                <StatusPill status={e.status} />
                            </div>
                            {e.image_url && (
                                <img src={e.image_url} alt={e.title} className="w-full h-48 object-cover mb-6" />
                            )}
                            <div className="space-y-1.5">
                                {e.location && <DetailRow label="Location" value={e.location} />}
                                <DetailRow label="Date" value={e.event_date} />
                            </div>
                        </div>
                    </div>
                </ExpandableScreenContent>
            </ExpandableScreen>
        </div>
    );
}

function DetailRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-baseline gap-2 pt-1">
            <span className="text-[9px] font-black uppercase tracking-widest text-white/30 flex-shrink-0 w-14">{label}</span>
            <span className="text-xs text-white/60 break-all">{value}</span>
        </div>
    );
}

// ─── Time In Review (Month mode range detail) ──────────────────────────────────

function TimeInReviewCard({
    range,
    review,
    expanded,
    onToggle,
    onAddNote,
    onDeleteNote,
    onBookingChange,
}: {
    range: { start: string; end: string };
    review: { bookings: CalendarBooking[]; events: CalendarEventRow[]; photos: CalendarPhoto[]; notes: CalendarNote[] };
    expanded: 'collapsed' | 'glance' | 'deep';
    onToggle: () => void;
    onAddNote: (date: string, note: string) => void;
    onDeleteNote: (id: string) => void;
    onBookingChange?: () => void;
}) {
    const isCollapsed = expanded === 'collapsed';
    const isDeep = expanded === 'deep';
    return (
        <div className="bg-white/[0.02]">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-white/[0.03] transition-colors"
            >
                <div className="text-left">
                    <p className="text-[9px] font-black uppercase tracking-[0.3em] text-white/40 mb-1">Time in Review</p>
                    <p className="text-sm font-bold text-white">
                        {review.bookings.length} bookings · {review.events.length} events · {review.photos.length} uploads
                        {review.notes.length > 0 ? ` · ${review.notes.length} notes` : ''}
                    </p>
                </div>
                {isCollapsed ? <ChevronDownIcon className="w-4 h-4 text-white/40" /> : <ChevronUpIcon className="w-4 h-4 text-white/40" />}
            </button>

            {!isCollapsed && (
                <div className="p-4 sm:p-6 pt-0 space-y-6">
                    <NotesSection range={range} notes={review.notes} onAdd={onAddNote} onDelete={onDeleteNote} />

                    <Section title="Bookings">
                        {review.bookings.length === 0 ? (
                            <EmptyLine>No bookings in this window.</EmptyLine>
                        ) : (
                            <div className="space-y-px">
                                {review.bookings.map(b => (
                                    <ExpandableBookingCard key={b.id} booking={b} onChange={onBookingChange} />
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Events">
                        {review.events.length === 0 ? (
                            <EmptyLine>No events in this window.</EmptyLine>
                        ) : (
                            <div className="space-y-px">
                                {review.events.map(e => (
                                    <ExpandableEventCard key={e.id} event={e} />
                                ))}
                            </div>
                        )}
                    </Section>

                    <Section title="Uploads">
                        {review.photos.length === 0 ? (
                            <EmptyLine>No photos in this window.</EmptyLine>
                        ) : (
                            <>
                                <div className={`grid gap-1 ${isDeep ? 'grid-cols-4 sm:grid-cols-6' : 'grid-cols-5 sm:grid-cols-8'}`}>
                                    {(isDeep ? review.photos : review.photos.slice(0, 16)).map(p => (
                                        <img key={p.id} src={p.thumbnail_url} alt={p.title} className="w-full aspect-square object-cover bg-white/5" />
                                    ))}
                                </div>
                                {!isDeep && review.photos.length > 16 && (
                                    <p className="text-white/40 text-[10px] mt-2">
                                        + {review.photos.length - 16} more —{' '}
                                        <button onClick={onToggle} className="underline hover:text-white">expand for full gallery</button>
                                    </p>
                                )}
                            </>
                        )}
                    </Section>

                    <button
                        onClick={onToggle}
                        className="w-full flex items-center justify-center gap-2 py-3 text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-colors bg-white/[0.03] hover:bg-white/[0.06]"
                    >
                        {isDeep ? 'Collapse to Glance' : 'Expand — Deeper Insights'}
                        <ArrowRightIcon className="w-3 h-3" />
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Notes Section ─────────────────────────────────────────────────────────────

function NotesSection({
    range,
    notes,
    onAdd,
    onDelete,
}: {
    range: { start: string; end: string };
    notes: CalendarNote[];
    onAdd: (date: string, note: string) => void;
    onDelete: (id: string) => void;
}) {
    const [drafting, setDrafting] = useState(false);
    const [draft, setDraft] = useState('');
    const [draftDate, setDraftDate] = useState(range.start);

    useEffect(() => { setDraftDate(range.start); }, [range.start, range.end]);

    const handleSave = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        onAdd(draftDate, trimmed);
        setDraft('');
        setDrafting(false);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 flex items-center gap-1.5">
                    <PencilSquareIcon className="w-3 h-3" />
                    Notes
                </p>
                {!drafting && (
                    <button
                        onClick={() => setDrafting(true)}
                        className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white transition-colors px-2 py-1 bg-white/5 hover:bg-white/10"
                    >
                        <PlusIcon className="w-3 h-3" />
                        Add
                    </button>
                )}
            </div>

            {drafting && (
                <div className="bg-white/5 p-3 mb-2 space-y-2">
                    <div className="flex items-center gap-2">
                        <label className="text-[9px] font-black uppercase tracking-widest text-white/40">On</label>
                        <input
                            type="date"
                            value={draftDate}
                            min={range.start}
                            max={range.end}
                            onChange={e => setDraftDate(e.target.value)}
                            className="bg-black text-white text-[11px] px-2 py-1 rounded-none focus:outline-none"
                        />
                    </div>
                    <textarea
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        rows={3}
                        autoFocus
                        placeholder="Note for this day…"
                        className="w-full bg-black text-white text-sm px-3 py-2 rounded-none placeholder-white/30 focus:outline-none resize-none"
                    />
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => { setDrafting(false); setDraft(''); }}
                            className="text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white px-3 py-1.5 bg-white/5 hover:bg-white/10 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={!draft.trim()}
                            className="text-[10px] font-black uppercase tracking-widest text-black bg-white hover:bg-white/80 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 transition-colors"
                        >
                            Save Note
                        </button>
                    </div>
                </div>
            )}

            {notes.length === 0 && !drafting ? (
                <p className="text-white/30 text-xs italic py-2">No notes in this window.</p>
            ) : (
                <div className="space-y-1">
                    {notes.map(n => (
                        <div key={n.id} className="flex items-start gap-3 p-2 bg-white/[0.03] group">
                            <div className="flex-1 min-w-0">
                                <p className="text-white/50 text-[10px] font-mono mb-1">{n.date}</p>
                                <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{n.note}</p>
                            </div>
                            <button
                                onClick={() => onDelete(n.id)}
                                className="text-white/30 hover:text-red-400 transition-colors p-1 opacity-0 group-hover:opacity-100"
                                title="Delete note"
                            >
                                <TrashIcon className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Primitives ────────────────────────────────────────────────────────────────

interface RecapSale {
    source: 'shop' | 'gallery' | 'tickets' | 'invoice' | 'street';
    customerName: string | null;
    customerEmail: string | null;
    amountCents: number;
    occurredAt: string;
    reference: string | null;
    invoiceId: string | null;
    invoiceNumber: string | null;
    crmContactId: string | null;
}
interface RecapDeployment {
    vercel_deployment_id: string;
    status: string;
    commit_sha: string | null;
    commit_message: string | null;
    url: string | null;
    created_at: string;
}
interface RecapResponse {
    date: string;
    sales: RecapSale[];
    totalCents: number;
    deployments: RecapDeployment[];
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const clock = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const SOURCE_LABEL: Record<RecapSale['source'], string> = {
    shop: 'SHOP', gallery: 'GALLERY', tickets: 'TICKETS', invoice: 'INVOICE', street: 'STREET',
};

/**
 * The day as a business record: every sale with who it came from and where the
 * money trail continues (invoice, CRM contact, payment reference), and what
 * shipped that day. Links use ?panel= URLs, which the admin opens directly.
 */
function DailyRecap({ recap, loading }: { recap: RecapResponse | null; loading: boolean }) {
    return (
        <Section title="Daily Recap">
            {loading ? (
                <EmptyLine>Loading recap…</EmptyLine>
            ) : !recap ? (
                <EmptyLine>Recap unavailable.</EmptyLine>
            ) : (
                <div className="space-y-4">
                    {/* Sales */}
                    {recap.sales.length === 0 ? (
                        <EmptyLine>No sales on this day.</EmptyLine>
                    ) : (
                        <div className="space-y-px">
                            <div className="flex items-center justify-between bg-white/[0.04] px-3 py-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-white/60">
                                    {recap.sales.length} sale{recap.sales.length === 1 ? '' : 's'}
                                </span>
                                <span className="text-sm font-black text-white tabular-nums">{money(recap.totalCents)}</span>
                            </div>
                            {recap.sales.map((s, i) => (
                                <div key={i} className="bg-white/[0.02] px-3 py-2.5">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="px-1.5 py-0.5 bg-white text-black text-[9px] font-black tracking-widest">
                                            {SOURCE_LABEL[s.source]}
                                        </span>
                                        <span className="text-xs font-bold text-white truncate">
                                            {s.customerName || s.customerEmail || 'Unknown customer'}
                                        </span>
                                        {s.customerName && s.customerEmail && (
                                            <span className="text-[10px] text-white/40 truncate">{s.customerEmail}</span>
                                        )}
                                        <span className="ml-auto text-xs font-black text-white tabular-nums">{money(s.amountCents)}</span>
                                        <span className="text-[10px] text-white/30 tabular-nums">{clock(s.occurredAt)}</span>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1.5">
                                        {s.invoiceId && (
                                            <a href="/admin?panel=invoices"
                                               className="text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors">
                                                Invoice {s.invoiceNumber || ''} →
                                            </a>
                                        )}
                                        {s.crmContactId ? (
                                            <a href="/admin?panel=crm"
                                               className="text-[10px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors">
                                                In CRM →
                                            </a>
                                        ) : (
                                            <span className="text-[10px] uppercase tracking-widest text-white/20">Not in CRM</span>
                                        )}
                                        {s.reference && (
                                            <span className="text-[10px] font-mono text-white/25 truncate" title={s.reference}>
                                                {s.reference.slice(0, 24)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Deployments — what shipped while the sales happened. */}
                    {recap.deployments.length > 0 && (
                        <div>
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 mb-1.5">
                                Deployments ({recap.deployments.length})
                            </p>
                            <div className="space-y-px">
                                {recap.deployments.map(d => (
                                    <div key={d.vercel_deployment_id} className="flex items-center gap-2 bg-white/[0.02] px-3 py-2">
                                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                            d.status === 'succeeded' ? 'bg-green-400' : 'bg-red-400'
                                        }`} />
                                        <span className="text-xs text-white/80 truncate flex-1">
                                            {d.commit_message?.split('\n')[0] || '(no commit message)'}
                                        </span>
                                        {d.commit_sha && (
                                            <span className="text-[10px] font-mono text-white/30">{d.commit_sha.slice(0, 7)}</span>
                                        )}
                                        <span className="text-[10px] text-white/30 tabular-nums">{clock(d.created_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Section>
    );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40">{title}</p>
                {action}
            </div>
            {children}
        </div>
    );
}

/**
 * Books an appointment from the day view, through the same endpoint the public
 * booking form uses — so availability blocking, admin notification and the
 * booking's lifecycle (it lands as pending, confirmable from its card below)
 * all behave exactly as if the client had requested it.
 */
function AddBookingForm({ date, onDone }: { date: string; onDone: () => void }) {
    const [f, setF] = useState({
        name: '', email: '', phone: '', business_name: '', event_type: '',
        start_time: '', end_time: '', location: '', notes: '', retainer: false,
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setF(prev => ({ ...prev, [k]: k === 'retainer' ? e.target.checked : e.target.value }));

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            const res = await fetch('/api/booking?action=request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...f, event_date: date }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            onDone();
        } catch (err: any) {
            setError(err?.message || 'Failed to create booking');
        } finally {
            setBusy(false);
        }
    }

    const field = "bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 w-full";
    return (
        <form onSubmit={submit} className="bg-white/[0.02] p-3 mb-2 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input required value={f.name} onChange={set('name')} placeholder="NAME *" className={field} style={{ borderRadius: 0 }} />
                <input required type="email" value={f.email} onChange={set('email')} placeholder="EMAIL *" className={field} style={{ borderRadius: 0 }} />
                <input value={f.phone} onChange={set('phone')} placeholder="PHONE" className={field} style={{ borderRadius: 0 }} />
                <input value={f.business_name} onChange={set('business_name')} placeholder="BUSINESS" className={field} style={{ borderRadius: 0 }} />
                <input required value={f.event_type} onChange={set('event_type')} placeholder="EVENT TYPE * (PORTRAIT, SHOW…)" className={field} style={{ borderRadius: 0 }} />
                <input value={f.location} onChange={set('location')} placeholder="LOCATION" className={field} style={{ borderRadius: 0 }} />
                <input type="time" value={f.start_time} onChange={set('start_time')} className={field} style={{ borderRadius: 0 }} aria-label="Start time" />
                <input type="time" value={f.end_time} onChange={set('end_time')} className={field} style={{ borderRadius: 0 }} aria-label="End time" />
            </div>
            <input value={f.notes} onChange={set('notes')} placeholder="NOTES" className={field} style={{ borderRadius: 0 }} />
            <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/60 cursor-pointer">
                    <input type="checkbox" checked={f.retainer} onChange={set('retainer')} className="accent-white" />
                    Retainer
                </label>
                <div className="flex items-center gap-3">
                    {error && <span className="text-[10px] uppercase tracking-widest text-red-400">{error}</span>}
                    <button
                        type="submit"
                        disabled={busy}
                        className="px-4 py-2 text-[10px] font-black uppercase tracking-widest bg-white text-black hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50"
                        style={{ borderRadius: 0 }}
                    >
                        {busy ? 'Booking…' : `Book ${date}`}
                    </button>
                </div>
            </div>
        </form>
    );
}

function EmptyLine({ children }: { children: React.ReactNode }) {
    return <p className="text-white/30 text-xs italic py-2">{children}</p>;
}

function StatusPill({ status }: { status: string }) {
    const map: Record<string, string> = {
        pending: 'bg-yellow-500/20 text-yellow-400',
        confirmed: 'bg-green-500/20 text-green-400',
        draft: 'bg-white/10 text-white/50',
        published: 'bg-green-500/20 text-green-400',
        declined: 'bg-red-500/20 text-red-400',
        cancelled: 'bg-red-500/20 text-red-400',
    };
    const cls = map[status] || 'bg-white/10 text-white/50';
    return (
        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 ${cls} flex-shrink-0`}>
            {status}
        </span>
    );
}
